import { capture } from '@parcel/analytics/browser';
import { toast } from '@parcel/ui';
import * as React from 'react';
import { ApiError } from './api.js';
import { type RecordRef, formatRecordParam } from './record-detail.js';
import { type WatchDataset, useWatchMutations, useWatchStatus } from './workspace-views.js';

export const WATCH_INTENT_TOAST =
  'Following. New filings on this record show up in your News feed.';

/** A record's watch dataset, or null for a contact, which cannot be followed. */
export function watchDatasetFor(record: RecordRef | null): WatchDataset | null {
  return record?.type === 'project' || record?.type === 'account' ? record.type : null;
}

/** The follow a dossier link asked for (follow=1): one write per record after the status read, then the flag leaves. */
export function useWatchIntent(input: {
  workspaceId: string | undefined;
  record: RecordRef | null;
  intent: boolean;
  consume: () => void;
}): void {
  const dataset = watchDatasetFor(input.record);
  const recordId = input.intent && dataset ? input.record?.id : undefined;
  const status = useWatchStatus(input.workspaceId, dataset ?? 'project', recordId);
  const { follow } = useWatchMutations(input.workspaceId, dataset ?? 'project');
  const handled = React.useRef<string | null>(null);
  const consumeRef = React.useRef(input.consume);
  consumeRef.current = input.consume;
  const mutate = follow.mutate;
  const settled = status.isSuccess || status.isError;
  const alreadyFollowed = status.data?.followed ?? false;
  const readFailed = status.isError;
  // A record that no longer exists (merged since the page was built) has nothing to follow.
  const missing = status.error instanceof ApiError && status.error.status === 404;

  React.useEffect(() => {
    if (!input.intent || !input.record) return;
    const key = formatRecordParam(input.record);
    if (handled.current === key) return;
    // A contact cannot be followed: the flag leaves and nothing else happens.
    if (!dataset || !recordId) {
      handled.current = key;
      consumeRef.current();
      return;
    }
    if (!input.workspaceId || !settled) return;
    handled.current = key;
    // A failed status read still follows: the write is idempotent and says whether anything changed.
    if (missing || (alreadyFollowed && !readFailed)) {
      consumeRef.current();
      return;
    }
    mutate([recordId], {
      onSuccess: (result) => {
        if (result.changed > 0) {
          capture('record_followed', {
            record_type: dataset,
            followed: true,
            count: 1,
            source: 'dossier',
          });
          toast.success(WATCH_INTENT_TOAST);
        }
      },
      onSettled: () => consumeRef.current(),
    });
  }, [
    alreadyFollowed,
    dataset,
    missing,
    input.intent,
    input.record,
    input.workspaceId,
    mutate,
    readFailed,
    recordId,
    settled,
  ]);
}
